import React, { useMemo } from 'react';
import { Combobox, Flex, Icon, Text } from '@servicetitan/anvil2';
import WarningIcon from '@servicetitan/anvil2/assets/icons/material/round/warning.svg';
import classNames from 'classnames';
import type { AudioDevice } from '../../types';
import { audioDeviceStrings } from '../audioDeviceStrings';
import styles from './AudioDeviceCombobox.module.css';

export interface AudioDeviceComboboxProps {
  /** Devices shown in the list */
  devices: readonly AudioDevice[];
  /** Currently active device id from MFE state */
  currentDeviceId: string | undefined;
  /** Called when the user picks a different device */
  onChange: (deviceId: string) => void;
  /** Field label (stacked above control, e.g. Input / Output) */
  label: string;
  /** Disables the control */
  disabled?: boolean;
  /** Shows list/empty loading UI inside the combobox */
  loading?: boolean;
  /**
   * Trigger placeholder when not loading (e.g. init failed with empty device list).
   * Defaults to select / loading copy from {@link audioDeviceStrings.placeholders}.
   */
  placeholderText?: string;
  /**
   * Initialization / guidance copy: neutral grey description under the field (no alert icon).
   * Prefer this for webphone init failures; use {@link errorMessage} for switch failures.
   */
  initializationDescription?: string | null;
  /** Error message displayed full-width below both combobox and button with warning icon. */
  errorMessage?: string | null;
  /** Optional test button rendered to the right of the combobox. */
  testButton: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

const itemToString = (item: AudioDevice | null) => item?.label ?? '';
const itemToKey = (item: AudioDevice | null) => item?.deviceId ?? '';

export const AudioDeviceCombobox: React.FC<AudioDeviceComboboxProps> = ({
  devices,
  currentDeviceId,
  onChange,
  label,
  disabled = false,
  loading = false,
  placeholderText,
  initializationDescription,
  errorMessage,
  testButton,
  className,
  'data-testid': dataTestId,
}) => {
  const selectedItem = useMemo(() => {
    if (currentDeviceId === undefined) {
      return null;
    }
    return devices.find((d) => d.deviceId === currentDeviceId) ?? null;
  }, [devices, currentDeviceId]);

  const isDisabled = disabled || loading;

  const placeholder = loading
    ? audioDeviceStrings.placeholders.loadingDevices
    : placeholderText ?? audioDeviceStrings.placeholders.selectDevice;

  return (
    <div className={styles.wrapper} data-testid={dataTestId}>
      <div className={styles.comboboxContainer}>
        <Combobox.Select
          className={classNames('w-100', className)}
          items={devices as AudioDevice[]}
          itemToString={itemToString}
          itemToKey={itemToKey}
          selectedItem={selectedItem}
          disableClearSelection
          loading={loading}
          disabled={isDisabled}
          onChange={(item: AudioDevice | null) => {
            if (item) {
              onChange(item.deviceId);
            }
          }}
        >
          <Combobox.SelectTrigger
            placeholder={placeholder}
            aria-label={label}
          />
          <Combobox.Content>
            {({ items }) => (
              <Combobox.List>
                {items.map((item, index) => (
                  <Combobox.Item key={item.deviceId} item={item} index={index}>
                    {item.label}
                  </Combobox.Item>
                ))}
              </Combobox.List>
            )}
          </Combobox.Content>
        </Combobox.Select>
      </div>
      {testButton}
      {initializationDescription ? (
        <Text
          size='small'
          className={classNames(
            styles.initializationDescription,
            styles.spanFullWidth,
          )}
          data-testid={
            dataTestId ? `${dataTestId}-initialization-description` : undefined
          }
        >
          {initializationDescription}
        </Text>
      ) : null}
      {errorMessage ? (
        <Flex
          gap='1'
          alignItems='start'
          className={styles.spanFullWidth}
          data-testid={dataTestId ? `${dataTestId}-error-message` : undefined}
        >
          <Icon svg={WarningIcon} size='small' className={styles.errorIcon} />
          <Text size='small' className={styles.errorText}>
            {errorMessage}
          </Text>
        </Flex>
      ) : null}
    </div>
  );
};
